import type { ForwardedRef, SVGProps } from 'react';
import { forwardRef } from 'react';
import type { ProgressBarProps } from 'react-aria-components';
import { ProgressBar } from 'react-aria-components';

import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useStyleProps } from '@infinex/ui-styles';

export type ProgressCircleProps = {
  /**
   * The prominence of the element.
   * @default 'default'
   */
  prominence?: 'low' | 'default' | 'high' | 'info';
  /**
   * The size of the element.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large' | 'xlarge';
} & Omit<ProgressBarProps, 'children' | 'className' | 'style'> &
  BaseStyleProps &
  UnsafeStyleProps;

const SIZE_MAP = {
  small: '16px',
  medium: '24px',
  large: '40px',
  xlarge: '64px',
};
const STROKE_MAP = {
  small: '2px',
  medium: '2px',
  large: '3px',
  xlarge: '5px',
};

/**
 * Shows the progression of a system operation such as downloading, uploading,
 * processing, etc.
 *
 * [Taurus docs: `ProgressCircle`](https://ui-sb.app.infinex.dev/?path=/docs/components-progresscircle--docs)
 */
export const ProgressCircle = forwardRef(function ProgressCircle(
  props: ProgressCircleProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {
    isIndeterminate,
    prominence = 'default',
    size = 'medium',
    ...otherProps
  } = props;

  const { className, style } = useStyleProps(props);

  return (
    <ProgressBar
      {...otherProps}
      isIndeterminate={isIndeterminate}
      ref={ref}
      className={className}
      style={{
        // @ts-expect-error CSS Variable declaration is valid
        '--diameter': SIZE_MAP[size],
        '--stroke-width': STROKE_MAP[size],
        '--radius': 'calc(var(--diameter) / 2)',
        '--offset-radius': 'calc(var(--radius) - var(--stroke-width) / 2)',
        '--circumference': `calc(var(--offset-radius) * pi * 2)`,
        ...style,
      }}
    >
      {(renderProps) => (
        <svg
          aria-hidden
          fill="none"
          focusable="false"
          role="img"
          className="size-(--diameter)"
          style={{
            // @ts-expect-error CSS Variable declaration is valid
            '--fraction': renderProps.percentage
              ? renderProps.percentage / 100
              : 0,
          }}
        >
          {/* track */}
          <Circle className="stroke-placeholder" />

          {/* indicator */}
          <Circle
            className={cn({
              'animate-spin': isIndeterminate,
              'transition-all': !isIndeterminate,

              'stroke-brand': prominence === 'high',
              'stroke-info': prominence === 'info',
              'stroke-emphasis': prominence === 'default',
              'stroke-secondary': prominence === 'low',
            })}
            style={{
              strokeDasharray: 'var(--circumference)',
              strokeLinecap: 'round',
              transformOrigin: 'center',

              ...(isIndeterminate
                ? {
                    strokeDashoffset: `calc(var(--circumference) - 0.75 * var(--circumference))`,
                  }
                : {
                    strokeDashoffset: `calc(var(--circumference) - var(--fraction) * var(--circumference))`,
                    transform: 'rotate(-90deg)',
                  }),
            }}
          />
        </svg>
      )}
    </ProgressBar>
  );
});

function Circle(props: SVGProps<SVGCircleElement>) {
  const { style, ...otherProps } = props;
  return (
    <circle
      style={{
        cx: 'var(--radius)',
        cy: 'var(--radius)',
        r: 'var(--offset-radius)',
        strokeWidth: 'var(--stroke-width)',
        ...style,
      }}
      {...otherProps}
    />
  );
}
